// The Accelerator home's cards, as Accelerator.dc.html draws them: Your goal (with the month of 12
// and its bar), the next session (with attendance and where they stand) and Your group (the group's
// own WhatsApp link). model.js works out what they say; copy.js has the words. The next session's Add to
// calendar and Your group's WhatsApp are the welcome checklist's twins: using either ticks its item there, and
// either Add to calendar turns the other to done (the Accelerator critique's member 12, 20 Oct).
import { useState } from 'react'
import { downloadIcs } from '../../lib/calendar.js'
import { Link } from '../../lib/router.jsx'
import { readOnlyControl } from '../../lib/viewAs.jsx'
import { ChatIcon } from '../../product/Icons.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { useChecklistTick } from '../../product/WelcomeChecklist.jsx'
import { COPY, SAVE_FAILED } from './copy.js'
import { goalError } from './model.js'
import { saveGoal } from './data.js'
import { LIMITS } from './limits.js'
import { SITE } from '../../config/site.js'

function Lock() {
  return (
    <svg className="acc-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

// Your goal. Locked, it's Adam's and the member's agreed words, read only. Before that the member
// drafts it here, and can change the draft until Adam locks it at their first session.
export function GoalCard({ view, goal, productId, personId, viewingAs, patch }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  const form = view.state === 'none' || editing

  function edit() {
    if (viewingAs) return
    setText(goal?.body || '')
    setLine(null)
    setEditing(true)
  }

  async function save(e) {
    e.preventDefault()
    if (viewingAs || busy) return
    const body = text.trim()
    if (!body || body.length > LIMITS.goal) { setLine(COPY.goalLength); return }
    setBusy(true)
    setLine(null)
    try {
      const row = await saveGoal({ goalId: goal?.id || null, personId, productId, body })
      patch(d => ({ ...d, goal: row }))
      setEditing(false)
      setText('')
    } catch (err) {
      setLine(goalError(err) || SAVE_FAILED)
    } finally {
      setBusy(false)
    }
  }

  // id: the welcome checklist's Draft your goal brings them here
  return (
    <section className="acc-card acc-goal" id="acc-goal" aria-labelledby="acc-goal-h">
      <div className="acc-goal-head">
        <h2 className="acc-eyebrow" id="acc-goal-h">{COPY.goal}</h2>
        {view.state === 'locked' && <span className="acc-goal-lock"><Lock />{view.locked}</span>}
        {view.state === 'draft' && <span className="acc-goal-lock">{COPY.draft}</span>}
      </div>
      {view.state !== 'none' && !editing && <p className="acc-goal-body">{view.body}</p>}
      {view.state === 'none' && <p className="acc-goal-intro">{view.intro}</p>}
      {form && (
        <form className="acc-goal-form" onSubmit={save} noValidate>
          <textarea aria-label={COPY.goalField} value={text} maxLength={LIMITS.goal} rows={3}
            onChange={e => { setText(e.target.value); setLine(null) }} readOnly={viewingAs} />
          <div className="acc-row">
            <button type="submit" className="acc-btn" aria-busy={busy || undefined} {...readOnlyControl(viewingAs)}>{COPY.saveGoal}</button>
            {editing && <button type="button" className="acc-btn-line" onClick={() => { setEditing(false); setLine(null) }}>{COPY.cancel}</button>}
          </div>
        </form>
      )}
      {view.state === 'draft' && !editing && (
        <button type="button" className="acc-btn-line acc-goal-change" onClick={edit} {...readOnlyControl(viewingAs)}>{COPY.changeGoal}</button>
      )}
      {line && <p className="acc-msg" role="alert">{line}</p>}
      {view.monthLine && (
        <div className="acc-term">
          <div className="acc-term-line">
            <span className="acc-term-month">{view.monthLine}</span>
            {view.side && <span className="acc-term-side">{view.side}</span>}
          </div>
          <div className="acc-term-bar" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${view.bar.length}, minmax(0, 1fr))` }}>
            {view.bar.map((on, i) => <span key={i} className={on ? 'acc-term-seg acc-term-seg--on' : 'acc-term-seg'} />)}
          </div>
        </div>
      )}
      {!view.monthLine && view.side && <span className="acc-term-side">{view.side}</span>}
    </section>
  )
}

// The big button under the goal: the 1 way into their accountability log from the home (Adam, 18 Oct, 16:12)
export function LogButton({ href }) {
  return (
    <Link className="acc-log-btn" to={href}>
      <span>{COPY.viewYourLog}</span>
      <svg className="acc-icon" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
    </Link>
  )
}

// The refocus card (model.js refocusView), on the home under the goal (on a phone, under the next session) and at the
// top of their log, only when they're behind: their goal back to them, what's slipped (on the home with "See which in
// your log", and any action carried into this month named), and 3 questions to ask themselves. Links only, so it's
// the same while staff view as the member.
export function RefocusCard({ view }) {
  if (!view) return null
  return (
    <section className={`acc-card acc-refocus acc-refocus--${view.key}`} aria-labelledby="acc-refocus-h">
      <h2 className="acc-refocus-h" id="acc-refocus-h">{view.heading}</h2>
      <p className="acc-refocus-goal">{view.goal}</p>
      <p className="acc-refocus-only">{view.only}</p>
      {view.facts && (
        <p className="acc-refocus-facts">
          {view.facts}
          {view.which && <> <Link className="acc-refocus-which" to={view.which.href}>{view.which.label}</Link></>}
        </p>
      )}
      {view.carried && <p className="acc-refocus-carried">{view.carried}</p>}
      <div className="acc-refocus-ask">
        <p className="acc-refocus-ask-h">{view.askH}</p>
        <ul className="acc-refocus-asks">
          {view.asks.map(q => <li key={q}>{q}</li>)}
        </ul>
      </div>
      {view.help && (
        <p className="acc-refocus-help">{view.help} <a href={`mailto:${SITE.helpEmail}`}>{SITE.helpEmail}</a></p>
      )}
    </section>
  )
}

// The next session: the date, when and which group, Join (from the time it opens), Add to calendar and All
// sessions, then their attendance and where they stand (the pill opens their accountability log), where the
// mastermind's settings show it (guaranteeOf in model.js), and their latest missed session still to watch (missed:
// model.js missedToWatch, member 9)
export function NextSessionCard({ view, standing, missed = null }) {
  const pd = useProductData()
  const tick = useChecklistTick()
  const [own, setAdded] = useState(false)
  const added = own || Boolean(pd?.data?.calendarAdded)
  function add() {
    downloadIcs(view.calendar)
    setAdded(true)
    if (pd?.patch) pd.patch(d => ({ ...d, calendarAdded: true }))
    tick('calendar')
  }
  return (
    <section className="acc-next" aria-labelledby="acc-next-h">
      <h2 className="acc-next-eyebrow" id="acc-next-h">{COPY.nextSession}</h2>
      {view
        ? (
          <>
            <div className="acc-next-when">
              <span className="acc-next-date">{view.date}</span>
              <span className="acc-next-line">{view.line}</span>
            </div>
            <div className="acc-next-actions">
              {view.join && (view.join.open
                ? <a className="acc-join acc-join--open" href={view.join.href} target="_blank" rel="noopener noreferrer">{view.join.label}</a>
                : <span className="acc-join">{view.join.label}</span>)}
              <button type="button" className="acc-cal" onClick={add}>
                {added ? COPY.added : COPY.addToCalendar}
              </button>
            </div>
            <Link className="acc-next-all" to={view.all}>{COPY.allSessions}</Link>
          </>
        )
        : <p className="acc-next-none">{COPY.noNextSession}</p>}
      {(standing || missed) && <div className="acc-next-rule" aria-hidden="true" />}
      {standing && (
        <>
          <div className="acc-standing">
            <span className="acc-standing-line">{standing.line}</span>
            {standing.pill && (standing.pill.href
              ? <Link className={`acc-pill acc-pill--${standing.pill.key}`} to={standing.pill.href}>{standing.pill.text}</Link>
              : <span className={`acc-pill acc-pill--${standing.pill.key}`}>{standing.pill.text}</span>)}
          </div>
          {standing.note && <p className="acc-next-note">{standing.note}</p>}
        </>
      )}
      {missed && (
        <p className="acc-next-missed">
          {COPY.missedSession(missed.day)} <Link className="acc-next-watch" to={missed.href}>{COPY.watchRecording}</Link>
        </p>
      )}
    </section>
  )
}

// Your group: the group's own WhatsApp (cohort_links, set per group on Recordings and dates)
export function GroupCard({ group, url, headingLevel = 'h2' }) {
  const [opened, setOpened] = useState(false)
  const tick = useChecklistTick()
  const H = headingLevel
  return (
    <section className="acc-card acc-group" aria-labelledby="acc-group-h">
      <H className="acc-h2" id="acc-group-h">{COPY.yourGroup}</H>
      <p className="acc-text">{group ? COPY.groupLine(group) : COPY.noGroup}</p>
      {group && url && (
        <a className="acc-wa" href={url} target="_blank" rel="noopener noreferrer" onClick={() => { setOpened(true); tick('whatsapp') }}>
          <ChatIcon size={17} />{opened ? COPY.openedWhatsApp : COPY.joinGroup(group)}
        </a>
      )}
      {group && !url && <p className="acc-text acc-text--small">{COPY.noWhatsApp}</p>}
    </section>
  )
}
